<template>
    <billing-panel class="billing-account-card">
        <div class="billing-account-card__header">
            <span class="billing-account-card__avatar">
                {{ initials }}
            </span>
            <billing-status-badge :status="status" />
            <v-btn
                icon
                variant="text"
                size="small"
                class="billing-account-card__menu"
            >
                <v-icon>$vuetify.icons.moreOptions</v-icon>
            </v-btn>
        </div>

        <h3 class="billing-account-card__title">
            {{ account.companyName }}
        </h3>
        <div class="billing-account-card__line">
            <v-icon size="15">$vuetify.icons.profile</v-icon>
            <span>{{ account.contactName || 'No contact' }}</span>
        </div>
        <div class="billing-account-card__line">
            <v-icon size="15">$vuetify.icons.email</v-icon>
            <span>{{ account.billingEmail || 'No email' }}</span>
        </div>

        <div class="billing-account-card__details">
            <div>
                <span>Plan</span>
                <strong>{{ planName }}</strong>
            </div>
            <div>
                <span>Billing</span>
                <strong>{{ billingCadence }}</strong>
            </div>
            <div>
                <span>Teams</span>
                <strong>{{ teamCount }}</strong>
            </div>
        </div>

        <div class="billing-account-card__meta">
            <span>
                <v-icon size="14">$vuetify.icons.team</v-icon>
                {{ teamCount }} teams
            </span>
            <span>
                <v-icon size="14">mdi-database-outline</v-icon>
                {{ datasetCount }} datasets
            </span>
        </div>
        <div
            v-if="showStorageUsage"
            class="billing-account-card__storage"
        >
            <div>
                <span>Storage</span>
                <strong>{{ storageUsageLabel }}</strong>
            </div>
            <v-progress-linear
                :model-value="storageUsagePercent"
                height="6"
                rounded
                color="primary"
            />
        </div>

        <div class="billing-account-card__actions">
            <v-btn color="primary" size="small" :href="account.url">
                Manage Account
                <v-icon end size="16">$vuetify.icons.next</v-icon>
            </v-btn>
            <v-btn
                variant="text"
                color="primary"
                size="small"
                :href="account.url"
            >
                View Billing
            </v-btn>
        </div>
    </billing-panel>
</template>

<script>
import BillingPanel from './BillingPanel.vue';
import BillingStatusBadge from './BillingStatusBadge.vue';

export default {
    name: 'BillingAccountCard',
    components: {
        BillingPanel,
        BillingStatusBadge,
    },
    props: {
        account: {
            type: Object,
            required: true,
        },
    },
    computed: {
        initials() {
            const name =
                this.account.companyName || this.account.contactName || '';
            const words = name.trim().split(/\s+/).filter(Boolean);
            if (!words.length) {
                return 'BA';
            }
            if (words.length === 1) {
                return words[0].slice(0, 2).toUpperCase();
            }
            return `${words[0][0]}${words[1][0]}`.toUpperCase();
        },
        status() {
            return this.account.subscriptionStatus || 'active';
        },
        planName() {
            return this.account.billingPlan && this.account.billingPlan.name
                ? this.account.billingPlan.name
                : 'No plan';
        },
        billingCadence() {
            const plan = this.account.billingPlan || {};
            if (!plan.frequency) {
                return 'Monthly';
            }
            return Number(plan.frequency) === 1
                ? 'Monthly'
                : `Every ${plan.frequency} months`;
        },
        teamCount() {
            return this.account.TeamCount || 0;
        },
        datasetCount() {
            return this.account.billingAssetCount || 0;
        },
        byoHosting() {
            return this.account.byo_hosting || {};
        },
        storageUsage() {
            return this.byoHosting.usage || {};
        },
        showStorageUsage() {
            return Boolean(this.byoHosting.is_byo_hosting_plan);
        },
        storageUsagePercent() {
            return Math.min(
                Number(this.storageUsage.storage_usage_percentage || 0),
                100
            );
        },
        storageUsageLabel() {
            const used = Number(this.storageUsage.storage_gb || 0);
            const included =
                this.byoHosting.limits &&
                this.byoHosting.limits.included_storage_gb;
            if (included === -1 || included === null || included === undefined) {
                return `${used.toFixed(2)} GB`;
            }
            return `${used.toFixed(2)} GB of ${included} GB`;
        },
    },
};
</script>

<style scoped>
.billing-account-card {
    min-height: 304px;
    padding: 20px;
}

.billing-account-card__header {
    align-items: flex-start;
    display: flex;
    gap: 12px;
}

.billing-account-card__avatar {
    align-items: center;
    background: linear-gradient(135deg, #00aeca, #3c64f4);
    border-radius: 16px;
    color: #ffffff;
    display: inline-flex;
    font-size: 18px;
    font-weight: 800;
    height: 48px;
    justify-content: center;
    width: 48px;
}

.billing-account-card__menu {
    margin-left: auto;
}

.billing-account-card__title {
    color: var(--billing-text);
    font-size: 18px;
    font-weight: 800;
    line-height: 1.3;
    margin: 16px 0 10px;
}

.billing-account-card__line,
.billing-account-card__meta {
    align-items: center;
    color: var(--billing-muted);
    display: flex;
    font-size: 13px;
    gap: 7px;
    min-width: 0;
}

.billing-account-card__line span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.billing-account-card__details {
    border-bottom: 1px solid var(--billing-border-soft);
    border-top: 1px solid var(--billing-border-soft);
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin: 18px 0 14px;
    padding: 14px 0;
}

.billing-account-card__details span {
    color: var(--billing-muted);
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.billing-account-card__details strong {
    color: var(--billing-text);
    display: block;
    font-size: 13px;
    line-height: 1.35;
    margin-top: 6px;
}

.billing-account-card__meta {
    gap: 18px;
}

.billing-account-card__meta span {
    align-items: center;
    display: inline-flex;
    gap: 6px;
}

.billing-account-card__storage {
    border-top: 1px solid var(--billing-border-soft);
    margin-top: 14px;
    padding-top: 14px;
}

.billing-account-card__storage div {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
}

.billing-account-card__storage span {
    color: var(--billing-muted);
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
}

.billing-account-card__storage strong {
    color: var(--billing-text);
    font-size: 13px;
}

.billing-account-card__actions {
    align-items: center;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    margin-top: 24px;
}

@media (max-width: 520px) {
    .billing-account-card__details {
        grid-template-columns: 1fr;
    }

    .billing-account-card__actions {
        align-items: stretch;
        flex-direction: column;
    }
}
</style>
